// AnnaGlow Component
// ==================
// CSS-only abstract glow representing Anna's warm, living presence.
// Replaces the PNG avatar with a distinctive gradient orb that "breathes".

import React from 'react';

interface AnnaGlowProps {
  /** Size of the glow orb */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Whether Anna is currently speaking (faster, more dynamic animation) */
  speaking?: boolean;
  /** Additional CSS classes for the outer container */
  className?: string;
}

const sizeMap = {
  sm: { outer: 48, inner: 36, blur: 8, auraSpread: 6 },
  md: { outer: 80, inner: 60, blur: 12, auraSpread: 10 },
  lg: { outer: 112, inner: 84, blur: 16, auraSpread: 14 },
  xl: { outer: 160, inner: 120, blur: 20, auraSpread: 18 },
};

const AnnaGlow: React.FC<AnnaGlowProps> = ({
  size = 'md',
  speaking = false,
  className = '',
}) => {
  const s = sizeMap[size];

  return (
    <div
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ width: s.outer, height: s.outer }}
      role="img"
      aria-label="Anna - din AI-tutor"
    >
      {/* Outer aura - soft ambient glow */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: `radial-gradient(circle at 50% 50%,
            rgba(224, 122, 95, 0.2) 0%,
            rgba(237, 165, 147, 0.1) 50%,
            transparent 70%
          )`,
          transform: `scale(1.4)`,
          animation: speaking
            ? 'anna-speak 1.8s ease-in-out infinite'
            : 'anna-aura 6s ease-in-out infinite',
        }}
      />

      {/* Middle layer - warm diffused ring */}
      <div
        className="absolute rounded-full"
        style={{
          width: s.outer + s.auraSpread,
          height: s.outer + s.auraSpread,
          top: -(s.auraSpread / 2),
          left: -(s.auraSpread / 2),
          background: `radial-gradient(circle at 40% 35%,
            transparent 40%,
            rgba(224, 122, 95, 0.08) 60%,
            rgba(123, 163, 140, 0.06) 80%,
            transparent 100%
          )`,
          filter: `blur(${s.blur * 0.5}px)`,
        }}
      />

      {/* Core orb - the main visual element */}
      <div
        className="absolute rounded-full"
        style={{
          width: s.inner,
          height: s.inner,
          top: (s.outer - s.inner) / 2,
          left: (s.outer - s.inner) / 2,
          background: `
            radial-gradient(circle at 35% 30%,
              rgba(249, 225, 219, 0.95) 0%,
              rgba(243, 195, 183, 0.85) 20%,
              rgba(224, 122, 95, 0.7) 45%,
              rgba(212, 90, 58, 0.5) 65%,
              rgba(123, 163, 140, 0.3) 85%,
              rgba(74, 144, 164, 0.15) 100%
            )
          `,
          boxShadow: `
            0 0 ${s.blur}px rgba(224, 122, 95, 0.3),
            0 0 ${s.blur * 2.5}px rgba(224, 122, 95, 0.15),
            inset 0 0 ${s.blur * 0.8}px rgba(255, 255, 255, 0.25)
          `,
          animation: speaking
            ? 'anna-speak 1.8s ease-in-out infinite'
            : 'anna-breathe 4s ease-in-out infinite',
        }}
      />

      {/* Highlight spot - gives depth and a living quality */}
      <div
        className="absolute rounded-full"
        style={{
          width: s.inner * 0.4,
          height: s.inner * 0.4,
          top: (s.outer - s.inner) / 2 + s.inner * 0.15,
          left: (s.outer - s.inner) / 2 + s.inner * 0.2,
          background: `radial-gradient(circle,
            rgba(255, 255, 255, 0.4) 0%,
            rgba(255, 255, 255, 0.1) 50%,
            transparent 100%
          )`,
          filter: `blur(${s.blur * 0.3}px)`,
        }}
      />

      {/* Speaking ring indicator */}
      {speaking && (
        <div
          className="absolute inset-0 rounded-full"
          style={{
            border: '2px solid rgba(224, 122, 95, 0.4)',
            animation: 'anna-aura 2s ease-in-out infinite',
          }}
        />
      )}
    </div>
  );
};

export default AnnaGlow;
